/**
 * Sales -> Orders handoff.
 *
 * Carries only catalog variant ids and quantities. It never carries money or
 * stock: the wizard re-fetches authoritative product, price, stock and image data
 * from the server, so a stale payload can never influence what the customer is
 * charged.
 *
 * sessionStorage (not a server draft) because the handoff is a same-tab, throwaway
 * convenience: it dies with the tab and - crucially - creates no database row and
 * reserves no stock. Reservation happens only when the order is actually created.
 *
 * Reading and clearing are separate on purpose. The wizard clears the payload only
 * once the products have been resolved, so a dropped connection can be retried
 * with a refresh instead of losing the basket. A refresh can only re-prefill the
 * wizard; it can never create an order.
 */

const HANDOFF_KEY = "bw:orderDraftFromSale";

export function writeOrderDraftHandoff(lines) {
  const payload = (lines || [])
    .map((line) => ({
      catalogVariantId: Number(line.catalogVariantId ?? line.variantId),
      quantity: Math.max(1, Number(line.quantity || 1)),
    }))
    .filter((line) => Number.isFinite(line.catalogVariantId) && line.catalogVariantId > 0);

  if (!payload.length) {
    return false;
  }

  try {
    window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ lines: payload }));
    return true;
  } catch {
    // Private mode or storage disabled: the handoff simply does not happen and the
    // wizard opens empty rather than failing.
    return false;
  }
}

export function readOrderDraftHandoff() {
  try {
    const raw = window.sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.lines)
      ? parsed.lines
          .map((line) => ({
            catalogVariantId: Number(line?.catalogVariantId),
            quantity: Math.max(1, Number(line?.quantity || 1)),
          }))
          .filter((line) => Number.isFinite(line.catalogVariantId) && line.catalogVariantId > 0)
      : [];
  } catch {
    return [];
  }
}

export function clearOrderDraftHandoff() {
  try {
    window.sessionStorage.removeItem(HANDOFF_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** The single shape of a wizard basket line, whatever added the product. */
export function toOrderBasketLine(item, quantity = 1) {
  return {
    catalogVariantId: Number(item.catalogVariantId),
    productTitle: item.productTitle,
    variantTitle: item.variantTitle || null,
    color: item.color || null,
    size: item.size || null,
    optionSummary: item.optionSummary || null,
    code: item.code || null,
    price: Number(item.price || 0),
    finalPrice: String(Number(item.price || 0)),
    availableQuantity: Number(item.availableQuantity || 0),
    imageUrl: item.thumbnailUrl || item.imageUrl || null,
    quantity,
  };
}

const describeName = (item) =>
  [item.productTitle, item.color, item.size].filter(Boolean).join(" · ") || item.productTitle;

/**
 * Combines the handed ids/quantities with the server's authoritative items.
 *
 * Every handed product ends up either as a basket line or as an explicit issue:
 * nothing is dropped silently. Quantities are capped at the stock that is free
 * right now (and the cap is reported).
 */
export function hydrateHandoffLines(handedLines, items) {
  const byId = new Map((items || []).map((item) => [Number(item.catalogVariantId), item]));
  const lines = [];
  const issues = [];

  for (const handed of handedLines || []) {
    const id = Number(handed.catalogVariantId);
    const item = byId.get(id);

    if (!item) {
      issues.push({ type: "not_found", id });
      continue;
    }

    const name = describeName(item);

    if (item.price == null) {
      issues.push({ type: "no_price", id, name });
      continue;
    }

    const available = Number(item.availableQuantity || 0);
    if (!item.canAddManually || available <= 0) {
      issues.push({ type: "no_stock", id, name });
      continue;
    }

    const existing = lines.find((line) => line.catalogVariantId === id);
    const wanted = Math.max(1, Number(handed.quantity || 1)) + (existing ? existing.quantity : 0);
    const quantity = Math.min(wanted, available);

    if (quantity < wanted) {
      issues.push({ type: "reduced", id, name, count: quantity });
    }

    if (existing) {
      existing.quantity = quantity;
    } else {
      lines.push(toOrderBasketLine(item, quantity));
    }
  }

  return { lines, issues };
}
